<template>
  <div class="xy-lucky-wheel" ref="componentRef">
    <lucky-grid
      :key="upKey"
      ref="luckyWheelRef"
      :width="wheelCfg.width"
      :height="wheelCfg.height"
      :rows="wheelCfg.rows"
      :cols="wheelCfg.cols"
      :prizes="wheelCfg.prizes"
      :blocks="wheelCfg.blocks"
      :buttons="wheelCfg.buttons"
      :defaultStyle="wheelCfg.defaultStyle"
      :activeStyle="wheelCfg.activeStyle"
      :defaultConfig="wheelCfg.defaultConfig"
      @end="endCallback" />
    <div v-if="showCheckBox" class="check-box" :style="checkBoxStyle">
      <template v-if="showCustomCheck">
        <img :style="checkboxImgStyle" :src="checkSrc" @click="handleCheckChange" />
      </template>
      <template v-else>
        <input type="checkbox" id="myCheckbox" @change="handleCheckChange" />
        <label for="myCheckbox"></label>
      </template>
      <span class="check-label">{{ checkLabel }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { info, defaultProps } from './config'
import { LuckyGrid } from '@lucky-canvas/vue'
import { useLucky } from '@/packages/components/hook/useLucky'
import { computeRatioPx, computeRatio } from '@/packages/plugins/utils'
import EmptyImg from '@/packages/components/business/luckyGrid/src/assets/img/empty.png'
import useAppStore from '@/h5/stores/app'

defineOptions({ name: info.name })
const props = defineProps(defaultProps)
const appStore = useAppStore ? useAppStore() : null

// 转盘配置
const wheelCfg = ref({
  width: '300px',
  height: '300px',
  rows: 60,
  cols: 60,
  prizes: [
    { x: 60 / 2 - 12 / 2, y: 0, col: 12, row: 12 },
    { x: 60 - 18 - 12 / 2, y: 9 - 12 / 2, col: 12, row: 12 },
    { x: 60 - 9 - 12 / 2, y: 18 - 12 / 2, col: 12, row: 12 },
    { x: 60 - 12, y: 60 / 2 - 12 / 2, col: 12, row: 12 },
    { x: 60 - 9 - 12 / 2, y: 60 - 18 - 12 / 2, col: 12, row: 12 },
    { x: 60 - 18 - 12 / 2, y: 60 - 9 - 12 / 2, col: 12, row: 12 },
    { x: 60 / 2 - 12 / 2, y: 60 - 12, col: 12, row: 12 },
    { x: 18 - 12 / 2, y: 60 - 9 - 12 / 2, col: 12, row: 12 },
    { x: 9 - 12 / 2, y: 60 - 18 - 12 / 2, col: 12, row: 12 },
    { x: 0, y: 60 / 2 - 12 / 2, col: 12, row: 12 },
    { x: 9 - 12 / 2, y: 18 - 12 / 2, col: 12, row: 12 },
    { x: 18 - 12 / 2, y: 9 - 12 / 2, col: 12, row: 12 }
  ],
  blocks: [
    { padding: '10px', background: '#869cfa', borderRadius: Infinity },
    { padding: '10px', background: '#e9e8fe', borderRadius: Infinity }
  ],
  buttons: [
    {
      x: 30 - 6 * 2.8,
      y: 30 - 6 * 2.8,
      col: 12 * 2.8,
      row: 12 * 2.8,
      fonts: [{ text: '开始', top: '43%' }]
    }
  ],
  defaultStyle: {
    borderRadius: Infinity,
    background: '#bac5ee'
  },
  activeStyle: {
    background: '#f7cf9f'
  },
  defaultConfig: {
    speed: 20,
    accelerationTime: 1500,
    decelerationTime: 1500
  }
})

const calculateButtonPosition = (diskWidth, diskHeight, buttonWidth, buttonHeight) => {
  const buttonCols = buttonWidth // 按钮的列数
  const buttonRows = buttonHeight // 按钮的行数
  const centerX = Math.floor((diskWidth - buttonWidth) / 2) // 中心X坐标
  const centerY = Math.floor((diskHeight - buttonHeight) / 2) // 中心Y坐标
  return { x: centerX, y: centerY, col: buttonCols, row: buttonRows }
}

const calculatePrizePositions = (prizesCount, diskWidth, diskHeight, prizeWidth, prizeHeight, offsetAngle) => {
  const positions = []
  const centerX = diskWidth / 2
  const centerY = diskHeight / 2
  const radius = Math.min(centerX, centerY) - Math.max(prizeWidth, prizeHeight) / 2

  for (let i = 0; i < prizesCount; i++) {
    let angle = (i / prizesCount) * 2 * Math.PI
    if (offsetAngle) {
      angle += (offsetAngle / 360) * 2 * Math.PI
    }
    // console.log('---(i / prizesCount) * 2 * Math.PI', (i / prizesCount) * 2 * Math.PI)
    const x = Math.round(centerX + radius * Math.cos(angle) - prizeWidth / 2)
    const y = Math.round(centerY + radius * Math.sin(angle) - prizeHeight / 2)
    positions.push({ x, y, col: prizeWidth, row: prizeHeight, background: props.prizesBackgroundSrc ? 'rgba(0,0,0,0)' : props.prizesBackgroundColor })
  }

  return positions
}

/** 初始化转盘配置 */
const initWheel = () => {
  // 转盘宽高
  wheelCfg.value.width = computeRatioPx(props.width)
  wheelCfg.value.height = computeRatioPx(props.height)
  /**
   * 转盘格点计算
   * 1. 根据转盘的宽高，和设置的奖项的宽高，每一个格点的大小为1，计算出转盘的行列数，和奖项的行列数
   * 2. 根据设置中间按钮的大小， 每一个格点的大小为1，计算出中间按钮的行列数，根据转盘的宽高计算出中间按钮的x,y坐标
   * 3. 根据奖项的长度等分圆周，计算出每个奖项的x,y坐标和行列数
   */
  const diskWidth = computeRatio(props.width) // 转盘宽度
  const diskHeight = computeRatio(props.height) // 转盘高度
  const prizeWidth = computeRatio(props.prizeWidth) // 奖项宽度
  const prizeHeight = computeRatio(props.prizeHeight) // 奖项高度
  const buttonWidth = computeRatio(props.buttonWidth) // 按钮宽度
  const buttonHeight = computeRatio(props.buttonHeight) // 按钮高度
  const offsetAngle = props.prizesOffsetAngle || 0 // 奖项偏移

  // 计算转盘的行列数和奖项的行列数
  const rows = diskWidth // 每个格点大小为1
  const cols = diskWidth // 每个格点大小为1

  // 计算按钮位置
  const buttonPosition = calculateButtonPosition(diskWidth, diskHeight, buttonWidth, buttonHeight)

  // 计算奖品位置
  // 设置一个默认的8个奖项
  let prizes = calculatePrizePositions(props.prizes.length ? props.prizes.length + props.blankPrizesNumber : 8, diskWidth, diskHeight, prizeWidth, prizeHeight, offsetAngle)
  wheelCfg.value.prizes = prizes

  // 设置转盘配置
  wheelCfg.value.rows = rows
  wheelCfg.value.cols = cols
  wheelCfg.value.buttons = [
    {
      x: buttonPosition.x,
      y: buttonPosition.y,
      col: buttonPosition.col,
      row: buttonPosition.row
    }
  ]
  // 设置中心图片
  if (props.buttonSrc) {
    wheelCfg.value.buttons[0].imgs = [{ src: props.buttonSrc, width: '100%', height: '100%' }]
    wheelCfg.value.buttons[0].background = ''
  }
  // 设置转盘背景
  if (props.blocksBackgroundColor) {
    wheelCfg.value.blocks = [{ padding: '0', background: props.blocksBackgroundColor, borderRadius: Infinity }]
  } else {
    wheelCfg.value.blocks = []
  }

  // 奖项背景颜色
  wheelCfg.value.defaultStyle.background = 'rgba(0,0,0,0)'
  wheelCfg.value.defaultStyle.borderRadius = props.prizesBorderRadius || 0
  // 奖项背景图 / 选中背景图
  if (props.prizesBackgroundSrc || props.prizesActiveBackgroundSrc) {
    prizes = prizes.map((item, index) => {
      const isBlank = index >= prizes.length - props.blankPrizesNumber
      item.imgs = [
        {
          src: props.prizesBackgroundSrc || EmptyImg,
          activeSrc: !isBlank && props.prizesActiveBackgroundSrc ? props.prizesActiveBackgroundSrc : '',
          width: '100%',
          height: '100%'
        }
      ]
      return item
    })
    wheelCfg.value.prizes = prizes
  }
  // 奖项选中背景颜色
  wheelCfg.value.activeStyle.background = props.prizesActiveBackgroundColor || ''

  // 旋转速度
  wheelCfg.value.defaultConfig.speed = Number(props.speed) || 20
  // 减速时间
  wheelCfg.value.defaultConfig.decelerationTime = Number(props.decelerationTime)
  // 开始时间
  wheelCfg.value.defaultConfig.accelerationTime = Number(props.accelerationTime)
}

/** 设置奖项 */
const setPrizes = (sortedAndMergedAwards, language) => {
  for (let i = 0; i <= props.prizes.length - props.blankPrizesNumber; i++) {
    const item = props.prizes[i]
    if (!item) {
      continue
    }
    const top = computeRatioPx(item.top)
    const languageItem = item.languages.find((item) => item.language === language)
    let src = languageItem?.src
    if (languageItem.srcFeMale && languageItem.srcMale) {
      src = appStore?.pageDataSource['data-source_userConstant']?.sex === 2 ? languageItem.srcFeMale : languageItem.srcMale
    } else if (languageItem.srcMale) {
      src = languageItem.srcMale
    }
    const id = sortedAndMergedAwards && sortedAndMergedAwards[i] ? sortedAndMergedAwards[i].id : 0
    const rewards = sortedAndMergedAwards && sortedAndMergedAwards[i] ? sortedAndMergedAwards[i].rewards : []
    const prizeImg = { src, top, width: computeRatioPx(item.width), height: computeRatioPx(item.height) }
    if (wheelCfg.value.prizes[i] && src) {
      wheelCfg.value.prizes[i].id = id
      wheelCfg.value.prizes[i].rewards = rewards
      if (wheelCfg.value.prizes[i]?.imgs?.length) {
        // 背景图在前，奖品图 push 到后面，保证奖品不被背景遮挡（lucky-canvas 按数组顺序后画在上）
        wheelCfg.value.prizes[i].imgs.push(prizeImg)
      } else {
        wheelCfg.value.prizes[i].imgs = [prizeImg]
      }
    }
  }
}

const {
  componentRef,
  luckyWheelRef,
  upKey,
  checkLabel,
  checkSrc,
  endCallback,
  showCustomCheck,
  checkboxImgStyle,
  handleCheckChange,
  checkBoxStyle,
  start,
  stop,
  getConfig,
  startRoulette,
  againRoulette,
  closeDialog,
  refreshMoneyBalances
} = useLucky(props, wheelCfg, initWheel, setPrizes)

defineExpose({
  start,
  stop,
  getConfig,
  startRoulette,
  againRoulette,
  closeDialog,
  refreshMoneyBalances
})
</script>

<style lang="scss" scoped>
.xy-lucky-wheel {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.check-box {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 45px;
  margin-top: 40px;
  box-sizing: border-box;
  .check-label {
    color: #fff;
  }
  #myCheckbox {
    display: none;
  }
  #myCheckbox + label::before {
    content: '';
    display: inline-block;
    width: 14px;
    height: 14px;
    margin-right: 10px;
    background: transparent; /* 默认背景颜色 */
    border-radius: 3px; /* 圆角 */
    vertical-align: middle;
    border: 1px solid #beabec;
  }

  /* 当复选框被选中时改变背景和添加勾选符号 */
  #myCheckbox:checked + label::before {
    background: transparent; /* 选中时的背景颜色 */
    content: '✓';
    color: #beabec; /* 勾选符号颜色 */
    font-size: 16px;
    text-align: center;
    line-height: 14px; /* 调整勾选符号的位置 */
  }
}
</style>
